import { ReactNode } from 'react';
import { useTranslate } from '@providers/TranslateProvider';

import { cx } from '@libs/classnames';
import { Button } from '@ui/components/Button';
import { ConfirmationButton, useConfimationButton } from '@ui/components/ConfirmationButton';
import {
  IconAnalysis,
  IconClose,
  IconCrown,
  IconDocument,
  IconDownload,
  IconDraw,
  IconFlag,
  IconList,
  IconRotate,
  IconSettings,
  IconSetupPosition,
  IconUpload,
  IconX,
} from '@ui/icons-buttons';

import { components } from '@features/shared/api/game';

import styles from './GameButtons.module.css';

/*
    ____,-------------------------------,____
    \   |          Компоненты           |   /
    /___|-------------------------------|___\
*/

type Props = {
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
};

export function ButtonCancelGame({
  className,
  onClick,
  children,
  disabled: _d,
}: Props & { children: ReactNode }) {
  const { t } = useTranslate();
  return (
    <ConfirmationButton
      className={className}
      icon={<IconX />}
      warning={t('@features/game/ui/GameButtons/Are you sure')}
      confirm={t('@features/game/ui/GameButtons/Confirm')}
      {...useConfimationButton(onClick)}
    >
      {children}
    </ConfirmationButton>
  );
}

export function ButtonDraw({ className, onClick, disabled }: Props) {
  const { t } = useTranslate();
  return (
    <ConfirmationButton
      className={className}
      icon={<IconDraw />}
      warning={t('@features/game/ui/GameButtons/Are you sure')}
      confirm={t('@features/game/ui/GameButtons/Confirm')}
      disabled={disabled}
      {...useConfimationButton(onClick)}
    >
      {t('@features/game/ui/GameButtons/Draw')}
    </ConfirmationButton>
  );
}

export function ButtonResign({ className, onClick, disabled }: Props) {
  const { t } = useTranslate();
  return (
    <ConfirmationButton
      className={className}
      icon={<IconFlag />}
      warning={t('@features/game/ui/GameButtons/Are you sure')}
      confirm={t('@features/game/ui/GameButtons/Confirm')}
      disabled={disabled}
      {...useConfimationButton(onClick)}
    >
      {t('@features/game/ui/GameButtons/Resign')}
    </ConfirmationButton>
  );
}

export function ButtonClaimVictory({ className, onClick, disabled }: Props) {
  const { t } = useTranslate();
  return (
    <ConfirmationButton
      className={className}
      icon={<IconCrown />}
      warning={t('@features/game/ui/GameButtons/Are you sure')}
      confirm={t('@features/game/ui/GameButtons/Confirm')}
      disabled={disabled}
      {...useConfimationButton(onClick)}
    >
      Заявить победу
    </ConfirmationButton>
  );
}

export function ButtonLeave({
  className,
  system,
  pill,
  disabled: _d,
  onClick,
}: Props & {
  system?: components['schemas']['TournamentSystem'] | 'pairing';
  pill?: boolean;
}) {
  const { t } = useTranslate();
  return (
    <Button className={className} onClick={onClick} variant={pill ? 'pill' : undefined}>
      {(() => {
        switch (system) {
          case 'OneOnOne':
            return t('@features/game/ui/GameButtons/Leave (to match)');
          case 'RoundRobin':
          case 'Knockout':
          case 'Swiss':
          case undefined:
            return t('@features/game/ui/GameButtons/Leave (to tournament)');
          case 'Seance':
          case 'pairing':
            return 'Вернуться';
          default:
            return t('@features/game/ui/GameButtons/Leave (default)');
        }
      })()}
    </Button>
  );
}

export function ButtonNotation({
  className,
  onClick,
  disabled,
  active,
}: Props & { active?: boolean }) {
  const { t } = useTranslate();
  return (
    <Button
      className={className}
      style={
        active
          ? {
              background: 'var(--theme-golden-active)',
              color: 'var(--theme-text-inverted)',
            }
          : undefined
      }
      icon={<IconList />}
      disabled={disabled}
      onClick={onClick}
    >
      {t('@features/game/ui/GameButtons/Notation')}
    </Button>
  );
}

export function ButtonPlayAgain({
  className,
  onClick,
  onSettings,
  disabled,
  children = 'Сыграть заново',
}: Props & { onSettings?: () => void; children?: ReactNode }) {
  return (
    <div className={cx(styles.play_again, className)}>
      <Button disabled={disabled} onClick={onClick}>
        {children}
      </Button>
      <Button disabled={disabled} variant="notext" icon={<IconSettings />} onClick={onSettings} />
    </div>
  );
}

export function ButtonRematch({
  className,
  onClick,
  disabled,
  highlight,
  children,
}: Props & { children: ReactNode; highlight?: boolean }) {
  return (
    <Button
      className={cx(highlight && styles.rematch_highlight, className)}
      icon={<IconRotate />}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function ButtonDownloadPgn({
  className,
  pill,
  children,
  disabled,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  return (
    <Button
      className={className}
      variant={pill ? 'pill' : children ? 'default' : 'notext'}
      icon={<IconDownload />}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function ButtonUpload({
  className,
  pill,
  children,
  disabled: _d,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  return (
    <Button
      className={className}
      variant={pill ? 'pill' : undefined}
      icon={<IconUpload />}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function ButtonRotate({
  className,
  pill,
  children,
  disabled: _d,
  onClick,
}: Props & { pill?: boolean; children?: ReactNode }) {
  const variant = pill ? (children ? 'pill' : 'pill-notext') : children ? 'default' : 'notext';
  return (
    <Button className={className} icon={<IconRotate />} onClick={onClick} variant={variant}>
      {children}
    </Button>
  );
}

export function ButtonAnalize({
  className,
  pill,
  children,
  disabled,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  const variant = pill ? (children ? 'pill' : 'pill-notext') : children ? 'default' : 'notext';

  return (
    <Button
      className={className}
      variant={variant}
      icon={<IconAnalysis />}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function ButtonClose({
  className,
  pill,
  children,
  disabled,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  const variant = pill ? (children ? 'pill' : 'pill-notext') : children ? 'default' : 'notext';

  return (
    <Button
      className={className}
      variant={variant}
      icon={<IconClose />}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export function ButtonSetupPosition({
  className,
  pill,
  children,
  disabled: _d,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  const variant = pill ? (children ? 'pill' : 'pill-notext') : children ? 'default' : 'notext';
  return (
    <Button className={className} variant={variant} icon={<IconSetupPosition />} onClick={onClick}>
      {children}
    </Button>
  );
}

export function ButtonFen({
  className,
  pill,
  children,
  disabled: _d,
  onClick,
}: Props & { children?: ReactNode; pill?: boolean }) {
  const variant = pill ? (children ? 'pill' : 'pill-notext') : children ? 'default' : 'notext';
  return (
    <Button className={className} variant={variant} icon={<IconDocument />} onClick={onClick}>
      {children}
    </Button>
  );
}
